<template>
  <div class="page-users">
    <div class="page-users__content">
      <div v-if="!isPhone" class="page-admin__table-header">
        <div class="page-admin__count">
          <span class="number-monospace">{{ users ? users.length : 0 }}</span>
          {{ ' ' + $t('basic-words.users') }}
        </div>
        <BaseRefreshButton class="page-admin__refresh" :size="40" :pending="pending" @refresh="refreshTenantUsers" />
      </div>
      <div class="page-platforms__table">
        <TableNormal
          :headers="headers"
          :items="users"
          :items-per-page="-1"
          class="elevation-1"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="pending"
          :loading-text="$t('page.admin-users.loading')"
          :no-data-text="$t('page.admin-users.no-users')"
          hide-default-footer
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ users ? users.length : 0 }}</span>
            {{ ' ' + $t('basic-words.users') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refreshTenantUsers" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>
          <template v-slot:item.firstName="{ item }">
            <div class="page-users__name">
              <span :style="{ fontWeight: isCurrentUser(item) ? 'bold' : '' }">
                {{ item.firstName + ' ' + item.lastName }}
              </span>
              <BaseTag v-if="isCurrentUser(item)" preset="secondary" tone="neutral">{{ $t('shared.me') }}</BaseTag>
            </div>
          </template>
          <template v-slot:item.role="{ item }">
            <BaseTag tone="neutral" preset="soft">{{ $t(`roles.${item.role}`) }}</BaseTag>
          </template>
          <template v-slot:item.permissions="{ item }">
            <div v-if="permissionTags[item.id]" class="page-users__permissions">
              <BaseTag
                v-for="label in permissionTags[item.id].shown"
                :key="label"
                v-tooltip="{ content: label, placement: 'top', offset: '8px', delay: 800 }"
                tone="neutral"
                preset="soft"
              >
                {{ label }}
              </BaseTag>
              <v-popover
                v-if="permissionTags[item.id].hidden.length"
                placement="top"
                trigger="hover"
                popoverClass="basic-tooltip"
              >
                <BaseTag tone="neutral" preset="soft">+{{ permissionTags[item.id].hidden.length }}</BaseTag>
                <template v-slot:popover>
                  <div v-for="label in permissionTags[item.id].hidden" :key="label">{{ label }}</div>
                </template>
              </v-popover>
            </div>
          </template>
          <template v-slot:item.actions="{ item }" v-if="isManager">
            <BaseMoreActions
              v-if="isPhone && rowActions(item).length"
              :title="item.firstName + ' ' + item.lastName"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-if="!canShowActionsMenu(item)"
                  v-tooltip="actionsDisabledTooltip(item)"
                  v-bind="attrs"
                  class="tooltip-wrapper"
                  @click.stop
                >
                  <BaseIcon name="more-horizontal" class="page-admin-opportunities__more disabled" />
                </div>
                <div v-else v-bind="attrs" v-on="on">
                  <BaseIcon name="more-horizontal" class="page-admin-opportunities__more" />
                </div>
              </template>
              <div class="page-admin-opportunities__actions-menu base-actions-menu">
                <ul>
                  <li v-if="canManageAccess(item)" @click="handleManageAccessClick(item)">
                    <BaseIcon :size="44" name="key" />
                    <span>{{ $t('page.admin-users.manage-access') }}</span>
                  </li>
                  <li v-if="canDeleteUser(item) && !item.isReserved" class="delete" @click="handleDeleteClick(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    <span>{{ $t('actions.delete') }}</span>
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
      </div>
    </div>

    <ConfirmationDialog ref="deleteUserDialog" :buttonText="$t('actions.delete')" @execute="executeDeleteUser">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="page.admin-users.delete-user-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
            <template v-slot:userEmail v-if="manipulatedObject">
              <span class="confirmation-dialog__user"> {{ manipulatedObject.email }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>

    <EditUserAccessDialog ref="accessDialog" @actionSucceeded="refreshTenantUsers" />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import EditUserAccessDialog from '@/components/page-admin-users/EditUserAccessDialog.vue';
import { PERMISSIONS, canEditPermissions, permissionI18nKey } from '@/models/permission';
import { canDeleteRole, isProtectedRole } from '@/models/role';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import { mapActions, mapGetters } from 'vuex';

// Beyond this, permissions collapse into a "+N" tag, so a row doesn't grow with each one granted.
const SHOWN_PERMISSION_TAGS = 2;

export default {
  name: 'PageAdminUsers',
  components: {
    BaseMoreActions,
    BaseIcon,
    BaseLoadingBar,
    BaseRefreshButton,
    BaseTag,
    ConfirmationDialog,
    EditUserAccessDialog,
    TableNormal,
  },
  data() {
    return {
      manipulatedObject: null,
      sortBy: ['firstName'],
      sortDesc: [false],
      headers: [
        { text: this.$t('page.admin-users.name'), value: 'firstName' },
        { text: this.$t('login.id'), value: 'email', phone: 'subtitle' },
        { text: this.$t('page.admin-invites.role'), value: 'role', phone: 'meta' },
        { text: this.$t('page.admin-users.permissions'), sortable: false, value: 'permissions', phone: 'meta' },
        { text: '', sortable: false, align: 'right', value: 'actions' },
      ],
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('auth', ['currentUser', 'isManager']),
    ...mapGetters('tenant', ['users', 'pending']),
    /** User id → the permission labels shown as tags, and the rest behind "+N". */
    permissionTags() {
      return (this.users ?? []).reduce((tags, user) => {
        const labels = (user.permissions ?? [])
          .filter((id) => PERMISSIONS.includes(id))
          .map((id) => this.$t(`${permissionI18nKey(id)}.label`))
          .sort((a, b) => a.localeCompare(b));
        tags[user.id] = { shown: labels.slice(0, SHOWN_PERMISSION_TAGS), hidden: labels.slice(SHOWN_PERMISSION_TAGS) };
        return tags;
      }, {});
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        ...(this.canManageAccess(item)
          ? [{ key: 'access', label: this.$t('page.admin-users.manage-access'), icon: 'key' }]
          : []),
        ...(this.canDeleteUser(item) && !item.isReserved
          ? [{ key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true }]
          : []),
      ].filter(() => this.canShowActionsMenu(item));
    },
    onRowAction(key, item) {
      if (key === 'access') this.handleManageAccessClick(item);
      else this.handleDeleteClick(item);
    },
    ...mapActions('tenant', ['getTenant']),
    ...mapActions('users', ['deleteUser']),
    refreshTenantUsers() {
      this.getTenant(this.currentUser.tenantId);
    },
    openDeleteUser(user) {
      this.manipulatedObject = user;
      this.$refs.deleteUserDialog.openDialog(user);
    },
    handleDeleteClick(item) {
      if (this.canDeleteUser(item)) {
        this.openDeleteUser(item);
      }
    },
    isCurrentUser(user) {
      return user.id === this.currentUser.id;
    },
    handleManageAccessClick(item) {
      if (this.canManageAccess(item)) {
        this.$refs.accessDialog.openDialog(item);
      }
    },
    async executeDeleteUser() {
      try {
        await this.deleteUser(this.manipulatedObject.id);
        this.$refs.deleteUserDialog.closeDialog();
        await this.refreshTenantUsers();
        this.$toast.success(this.$t('page.admin-users.user-deleted'));
      } catch (error) {
        this.$toast.error(this.$t('page.admin-users.error-deleting-user'));
      }
    },
    canDeleteUser(user) {
      // Delete and edit-role now share the same authorization on the backend.
      // Identity check (no self-delete) lives here; role-level policy in canDeleteRole.
      if (!user || user.id === this.currentUser.id) return false;
      return canDeleteRole(this.currentUser.role, user.role);
    },
    /**
     * Whoever may edit a user's role may edit their permissions too (pinned in permission.spec), so
     * the panel opens on the permissions rule and shows the role read-only where it can't change.
     */
    canManageAccess(user) {
      return canEditPermissions(this.currentUser, user);
    },
    canShowActionsMenu(user) {
      return this.canDeleteUser(user) || this.canManageAccess(user);
    },
    /**
     * Picks the precise reason the action menu is disabled. Order matters:
     * - self first (most specific — "you can't act on your own account")
     * - then admin-tier ("administrator account" — covers peer-locked admins
     *   and any other ungrantable admin target)
     * - then the generic "higher role" fallback
     */
    actionsDisabledTooltip(user) {
      if (user.id === this.currentUser.id) {
        return this.$t('error.cannot-manage-self');
      }
      if (isProtectedRole(user.role)) {
        return this.$t('error.cannot-manage-protected-role');
      }
      return this.$t('error.cannot-manage-higher-role');
    },
  },
};
</script>

<style lang="scss">
.page-users {
  &__header {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    justify-content: space-between;
    display: flex;
    align-items: center;
    min-height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;
    padding: 0px 32px;
  }
  &__head {
    display: flex;
    align-items: center;
  }
  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }
  &__name {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
  // One line: long labels are cut short (full label on hover), as in BaseLabelsMenu.
  &__permissions {
    display: flex;
    gap: 4px;
    min-width: 0;

    .base-tag {
      min-width: 0;
      max-width: 200px;
    }
    .base-tag__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .v-popover {
      flex-shrink: 0;
    }
  }
  &__table {
    .v-data-table.theme--light tbody {
      cursor: default;
    }
    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }
  }
  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }
}
.page-admin-opportunities__more.disabled {
  color: $color-slate-200;
  pointer-events: none;
}
.tooltip-wrapper {
  display: inline-block;
  width: auto;
  max-width: 40px;
  margin-right: 16px;
  padding-left: 8px;
}
</style>
